import * as Sentry from "@sentry/react";

/**
 * Error reporting for the browser.
 *
 * Optional by construction: without VITE_SENTRY_DSN nothing is initialised and
 * the application runs normally. A monitoring dependency that can stop the
 * login screen from loading is worse than no monitoring.
 *
 * Nothing about a person leaves this application. It shows the salaries and
 * personal records of 85 employees; an error report is not a reason to hand a
 * third party their OIB, their email address or their IP.
 *
 * Note that VITE_* values are inlined into the bundle and are therefore
 * public. This must be the frontend project's DSN, never the backend's.
 */
export function configureSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,

    // No IP address, no cookies, no user identity attached to events.
    sendDefaultPii: false,

    // No session replay integration at all. A recording of somebody's payslip
    // is not something to store with a third party, and an integration that
    // is never loaded cannot be switched on by a default changing under us.
    integrations: [],

    // Breadcrumbs record what the user did before an error. Console output and
    // network payloads both routinely carry personal data, so neither is kept.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console") {
        return null;
      }
      if (breadcrumb.data) {
        delete breadcrumb.data.body;
        delete breadcrumb.data.response_body_size;
      }
      return breadcrumb;
    },

    beforeSend(event) {
      // Belt and braces over sendDefaultPii: strip anything identifying that
      // reached the event by another route.
      delete event.user;
      delete event.server_name;

      if (event.request) {
        // HTTP bodies never leave the browser.
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;

        if (event.request.url) {
          event.request.url = event.request.url.replace(
            /([?&](token|access_token|code|email)=)[^&]+/gi,
            "$1[uklonjeno]",
          );
        }
      }

      return event;
    },
  });

  return true;
}
